import React, { useEffect } from "react";
import { Phone, Mail, Flame, Quote } from "lucide-react";
import SubpageHero from "@/components/SubpageHero";
import SubpageLayout from "@/components/SubpageLayout";
import SectionNumber from "@/components/SectionNumber";
import UpcomingEvents from "@/components/UpcomingEvents";
import PageGallery from "@/components/PageGallery";
import Embers from "@/components/Embers";
import { SITE } from "@/lib/data";
import useScrollReveal from "@/hooks/useScrollReveal";

const DISHES = [
  {
    name: "Kemencés ételek",
    img: "/images/nkl5175x.jpg",
    desc: "A tűz reggeltől ég, az ételek előtted készülnek. Ropogós arany kéreg, omlós hús.",
    price: "20 000 Ft / fő",
  },
  {
    name: "Grill ételek",
    img: "/images/jd0m09ac.jpg",
    desc: "Friss, szezonális alapanyagok parázs felett — egyszerű, de mély ízek.",
    price: "20 000 Ft / fő",
  },
  {
    name: "Szabadtűzi ételek",
    img: "/images/7vofl2kc.jpg",
    desc: "Nyílt láng, állvány, vasháromláb. Hagyományos technikák, modern ízek.",
    price: "20 000 Ft / fő",
  },
  {
    name: "Balkáni ételek",
    img: "/images/m6wnikb9.jpg",
    desc: "Hagyományos bosnyák ételek marhahúsból, föld alatt és felett sütve. Török kávé homokban tálalva.",
    price: "20 000 Ft / fő",
  },
  {
    name: "Magyaros ételek",
    img: "/images/f7m9bya5.jpg",
    desc: "Tradíció a parázson — pörkölt, gulyás, bográcsos egytálételek.",
    price: "20 000 Ft / fő",
  },
  {
    name: "Olasz ételek",
    img: "/images/fquinro5.jpg",
    desc: "Kemencés pizza, focaccia, ropogós héj és lassan érlelt tészta.",
    price: "20 000 Ft / fő",
  },
];

const NIGHTS = [
  {
    name: "Magyaros Est",
    date: "2026. március 27. — 16:00",
    img: "/images/zohq6t2d.jpg",
    desc: "Tradicionális magyar ízek parázs felett és kemencében.",
    price: "15 000 Ft / fő",
  },
  {
    name: "Bosnyák Est",
    date: "2026. április 7. — 16:00",
    img: "/images/m6wnikb9.jpg",
    desc: "Hagyományos bosnyák ételek marhahúsból, föld alatt és felett sütve. Török kávé homokban tálalva.",
    price: "15 000 Ft / fő",
  },
  {
    name: "Olasz Est",
    date: "2026. április 27. — 16:00",
    img: "/images/fquinro5.jpg",
    desc: "Toszkán fogások parázs felett, kemencében sült focaccia, hosszú asztal a fák alatt.",
    price: "15 000 Ft / fő",
  },
  {
    name: "Grill Est",
    date: "2026. május 27. — 16:00",
    img: "/images/d5l3w5vu.jpg",
    desc: "A grill nem főz — emlékezik. Lassú parázs, friss zöldek és érett borok.",
    price: "15 000 Ft / fő",
  },
];

const ANIMALS = ["Di", "Dra", "Mang", "Tequila", "Fiona", "Kutyacsapat"];

const GALLERY_IMGS = [
  { src: "/images/eskjx3c6.jpg", label: "Asztal körül" },
  { src: "/images/d5l3w5vu.jpg", label: "Tálalás" },
  { src: "/images/f7m9bya5.jpg", label: "Bárány" },
  { src: "/images/fquinro5.jpg", label: "Kemence" },
  { src: "/images/m6wnikb9.jpg", label: "Balkáni est" },
  { src: "/images/7vofl2kc.jpg", label: "Parázs" },
  { src: "/images/zohq6t2d.jpg", label: "Tepsi" },
  { src: "/images/nkl5175x.jpg", label: "Tűz" },
];

export default function FalusiVendegasztal() {
  useScrollReveal();
  useEffect(() => {
    document.title = "Falusi vendégasztal · Pakkaland";
  }, []);

  return (
    <SubpageLayout testId="page-falusi-vendegasztal">
      <SubpageHero
        crumb="Falusi vendégasztal"
        eyebrow="Vendégasztal"
        title="Falusi vendégasztal"
        tagline="Ahol a természet ölel, és az ízek mesélnek."
        image="/images/eskjx3c6.jpg"
      />

      {/* Intro — spiffed up */}
      <section
        id="bemutatkozas"
        className="relative bg-white py-24 sm:py-32 overflow-hidden"
      >
        <SectionNumber n="01" />

        {/* Warm background blob */}
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-[0.07] blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, #D90429 0%, #FFA64D 55%, transparent 75%)",
          }}
        />
        <Embers count={8} variant="light" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center relative">
          {/* LEFT: Image stack */}
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="relative aspect-[4/5] overflow-hidden bg-pkbeige">
                <img
                  src="/images/7vofl2kc.jpg"
                  alt="Falusi vendégasztal"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pkcharcoal/35 via-transparent to-transparent" />
                <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm px-3 py-2 shadow-md">
                  <div className="text-[0.55rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold">
                    Vezseny
                  </div>
                  <div className="font-serif italic text-pkcharcoal text-base leading-none mt-1">
                    2014 óta
                  </div>
                </div>
              </div>

              {/* Polaroid overlap */}
              <div className="hidden sm:block absolute -bottom-10 -right-6 w-2/5 aspect-[3/4] overflow-hidden border-[10px] border-white shadow-2xl bg-pkbeige rotate-[3deg] hover:rotate-0 transition-transform duration-700">
                <img
                  src="/images/d5l3w5vu.jpg"
                  alt="Tálalás"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Ember line */}
              <div className="absolute -left-3 sm:-left-5 top-12 bottom-12 w-px bg-gradient-to-b from-pkred via-pkred/30 to-transparent">
                <span className="absolute -top-1 -left-[3px] w-[7px] h-[7px] rounded-full bg-pkred shadow-[0_0_12px_rgba(217,4,41,0.8)]" />
              </div>
            </div>
          </div>

          {/* RIGHT: Copy */}
          <div className="lg:col-span-7 reveal">
            <span className="eyebrow">Bemutatkozás</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-[3.75rem] mt-6 leading-[1.02]">
              Mi a Pakkaland<br />
              <span className="italic font-light text-pkred">
                falusi vendégasztal?
              </span>
            </h2>

            {/* Pull quote */}
            <div className="mt-10 relative pl-8 sm:pl-12 max-w-2xl">
              <Quote
                className="absolute -top-2 left-0 w-6 h-6 sm:w-8 sm:h-8 text-pkred/80"
                strokeWidth={1.2}
                fill="currentColor"
              />
              <p className="font-serif text-xl sm:text-2xl lg:text-[1.7rem] leading-[1.45] text-pkcharcoal font-light">
                Egy hely, ahol a természet ízét{" "}
                <span className="text-pkred">ízleled</span>, az ízek mosolyt
                csalnak, s az állatok barátaink lesznek.
              </p>
              <div className="mt-5 flex items-center gap-3">
                <span className="block w-8 h-px bg-pkred" />
                <span className="text-[0.65rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold">
                  A házigazdák
                </span>
              </div>
            </div>

            <p className="mt-10 text-pkcharcoal/70 text-base sm:text-lg leading-relaxed max-w-2xl">
              Képzeld el friss, házias ételeket, a természet illatát a
              levegőben, s kedves állatokat – alpakkák (Di és Dra), póni
              (Mang), lovak (Tequila, Fiona) és kutyacsapat – körülötted.
              Falusi vendégasztalunk Vezseny csendes falujában várja a
              különleges élményekre vágyókat. Nálunk nemcsak a hasadat, de a
              lelkedet is jól lakathatod. Kemencében sütött falatok, grillen
              nyársbárány, balkáni ételek szezonális alapanyagokból.
            </p>

            {/* Animal names as chips */}
            <div className="mt-8">
              <div className="text-[0.65rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold mb-3">
                A család tagjai
              </div>
              <div className="flex flex-wrap gap-2">
                {ANIMALS.map((a) => (
                  <span
                    key={a}
                    className="px-3 py-1.5 border border-pkash text-[0.7rem] tracking-[0.15em] uppercase text-pkcharcoal/75 font-semibold hover:border-pkred hover:text-pkred transition-colors cursor-default"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-px bg-pkash">
              {[
                { n: "6", l: "Ételkategória", I: Flame },
                { n: "4", l: "Tematikus est", I: Flame },
                { n: "20K", l: "Ft / fő-től", I: Flame },
              ].map((s) => (
                <div
                  key={s.l}
                  className="bg-white p-5 sm:p-6 group hover:bg-pkbeige transition-colors duration-500"
                >
                  <s.I
                    className="w-4 h-4 text-pkred/60 group-hover:text-pkred group-hover:scale-110 transition-all duration-500"
                    strokeWidth={1.6}
                  />
                  <div className="font-serif text-3xl sm:text-4xl text-pkcharcoal font-medium mt-3 leading-none group-hover:text-pkred transition-colors duration-500">
                    {s.n}
                  </div>
                  <div className="text-[0.65rem] tracking-[0.2em] uppercase text-pkcharcoal/55 mt-3">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Choose food */}
      <section className="relative bg-pkbeige py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="02" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <span className="eyebrow center">Kóstold meg</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Választható<br />
              <span className="italic text-pkred">ételek.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DISHES.map((d, i) => (
              <article
                key={d.name}
                data-testid={`dish-card-${i}`}
                className="card-fire bg-white group reveal"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={d.img}
                    alt={d.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                  />
                </div>
                <div className="p-6 sm:p-7">
                  <h3 className="font-serif text-2xl text-pkcharcoal leading-tight">
                    {d.name}
                  </h3>
                  <p className="text-pkcharcoal/65 text-sm leading-relaxed mt-3">
                    {d.desc}
                  </p>
                  <div className="mt-6 pt-5 border-t border-pkash flex items-center justify-between">
                    <span className="text-pkred font-semibold text-base">
                      {d.price}
                    </span>
                    <a
                      href={SITE.phoneHref}
                      className="text-[0.7rem] tracking-[0.2em] uppercase text-pkcharcoal hover:text-pkred font-semibold transition-colors"
                    >
                      Asztalt foglalok →
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Tematikus estek */}
      <section className="relative bg-pkcharcoal py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="03" variant="dark" />
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            background:
              "radial-gradient(ellipse at 80% 20%, rgba(217,4,41,0.35) 0%, transparent 50%)",
          }}
        />
        <div className="grain opacity-10" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16 reveal">
            <span className="eyebrow">Csatlakozz hozzánk</span>
            <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Tematikus<br />
              <span className="italic text-pkred">estek.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {NIGHTS.map((n, i) => (
              <article
                key={n.name}
                data-testid={`night-card-${i}`}
                className="relative group overflow-hidden aspect-[16/10] reveal"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img
                  src={n.img}
                  alt={n.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pkcharcoal via-pkcharcoal/40 to-transparent" />
                <div className="absolute inset-0 p-7 sm:p-8 flex flex-col justify-end">
                  <div className="self-start date-badge mb-3">
                    {n.date}
                  </div>
                  <h3 className="font-serif text-white text-3xl sm:text-4xl mt-1 leading-tight">
                    {n.name}
                  </h3>
                  <p className="text-white/75 text-sm mt-3 max-w-md">{n.desc}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-pkred font-semibold">{n.price}</span>
                    <a
                      href={SITE.phoneHref}
                      data-testid={`night-cta-${i}`}
                      className="text-white text-[0.7rem] tracking-[0.2em] uppercase font-semibold hover:text-pkred transition-colors"
                    >
                      Jelentkezem →
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <PageGallery
        sectionN="04"
        bg="bg-white"
        eyebrow="Ilyenek vagyunk"
        title={
          <>
            Képek &<br />
            <span className="italic text-pkred">videók.</span>
          </>
        }
        subtitle="Vidéki oázisunk elválaszthatatlan részei ők. Barátságos csapatunk teszi teljessé az élményt kicsiknek és nagyoknak egyaránt."
        images={GALLERY_IMGS}
      />

      <BookingProcess sectionN="05" />
    </SubpageLayout>
  );
}

export function BookingProcess({ sectionN = "04" }) {
  return (
    <section className="relative bg-pkbeige py-24 sm:py-32 overflow-hidden">
      <SectionNumber n={sectionN} />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <span className="eyebrow center">Foglalás</span>
          <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            Foglald le<br />
            <span className="italic text-pkred">az estét.</span>
          </h2>
          <p className="mt-6 text-pkcharcoal/70 text-base sm:text-lg">
            A foglalás telefonon vagy emailben történik. Hívj minket, vagy
            küldj egy üzenetet, és visszaigazolunk.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-pkash">
          {[
            {
              n: "01",
              t: "Válassz menüt",
              d: "Válassz menüinkből vagy kérj egyedi menüt. Ha kérdésed van, hívj minket telefonon, WhatsApp-on vagy üzenj nekünk emailben.",
            },
            {
              n: "02",
              t: "Add meg az adataid",
              d: "Telefonban vagy emailben add meg az időpontot, létszámot és igényeket.",
            },
            {
              n: "03",
              t: "Foglald le az estét",
              d: "Hamarosan visszaigazolunk és egyeztetjük a részleteket.",
            },
          ].map((s, i) => (
            <div
              key={s.n}
              className="bg-white p-8 sm:p-10 reveal"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="text-pkred font-serif text-5xl font-extralight">
                {s.n}
              </div>
              <h3 className="font-serif text-xl text-pkcharcoal mt-4">{s.t}</h3>
              <p className="text-pkcharcoal/65 text-sm mt-3 leading-relaxed">
                {s.d}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
          <a href={SITE.phoneHref} className="btn-fire">
            <Phone className="w-4 h-4" strokeWidth={1.8} />
            Hívlak titeket
          </a>
          <a href={SITE.emailHref} className="btn-ghost-dark">
            <Mail className="w-4 h-4" strokeWidth={1.8} />
            Email-ben írok
          </a>
        </div>

        <div className="mt-14 max-w-3xl mx-auto bg-white p-6 sm:p-8 border border-pkash">
          <div className="text-[0.65rem] tracking-[0.3em] uppercase text-pkred font-semibold mb-3">
            Foglalási feltételek
          </div>
          <ul className="text-sm text-pkcharcoal/75 space-y-2 list-disc pl-5">
            <li>50% előleg fizetése átutalással vagy bankkártyával.</li>
            <li>48 órával az időpont előtt a lemondás ingyenes.</li>
            <li>48 órán belül a lemondás díja az előleg teljes összege.</li>
            <li>
              A foglalás csak visszaigazolás után érvényes — kérjük, várd meg
              válaszunkat.
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
